import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { Plus, ExternalLink } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { KrSliderRow } from "@/features/objectives/kr-slider-row";
import { CreateKeyResultDialog } from "@/features/key-results/create-key-result-dialog";
import { useKeyResults } from "@/hooks/use-key-results";
import { useUsers } from "@/hooks/use-users";
import { useCurrentUser, isSupervisorOrAbove } from "@/hooks/use-current-user";
import { getEntityPermissions } from "@/lib/permissions";
import { formatRelativeTime } from "@/lib/date";
import type { KeyResult, Quarter } from "@/types/entities";

const QUARTER_ORDER: Record<Quarter, number> = {
  Q1: 1,
  Q2: 2,
  Q3: 3,
  Q4: 4,
};

function sortKeyResults(keyResults: KeyResult[]): KeyResult[] {
  return [...keyResults].sort((a, b) => {
    const quarterDiff = QUARTER_ORDER[a.quarter] - QUARTER_ORDER[b.quarter];
    if (quarterDiff !== 0) return quarterDiff;
    return a.title.localeCompare(b.title);
  });
}

interface ObjectiveKrListProps {
  objectiveId: string;
  ownerId: string;
  year?: number;
}

export function ObjectiveKrList({ objectiveId, ownerId, year }: ObjectiveKrListProps) {
  const { currentUser } = useCurrentUser();
  const needsHierarchyCheck = isSupervisorOrAbove(currentUser?.role);
  const { data: usersData } = useUsers(needsHierarchyCheck ? { limit: 100 } : { limit: 0 });
  const allUsers = useMemo(() => usersData?.data ?? [], [usersData]);
  const { data, isLoading } = useKeyResults({ objective_id: objectiveId });

  const sortedKrs = useMemo(() => {
    if (!data?.data) return [];
    return sortKeyResults(data.data);
  }, [data]);

  const { canEdit } = getEntityPermissions({
    currentUser,
    ownerId,
    users: allUsers,
  });

  const lastUpdated = useMemo(() => {
    if (sortedKrs.length === 0) return null;
    let latest = sortedKrs[0]!;
    for (const kr of sortedKrs) {
      if (Date.parse(kr.updated_at) > Date.parse(latest.updated_at)) latest = kr;
    }
    return latest;
  }, [sortedKrs]);

  const lastUpdatedAuthorName = useMemo(() => {
    if (!lastUpdated) return null;
    return allUsers.find((u) => u.id === lastUpdated.owner_id)?.name ?? null;
  }, [lastUpdated, allUsers]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (sortedKrs.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-sm italic text-[hsl(var(--muted-foreground))]">
          No key results yet
        </p>
        {canEdit && (
          <CreateKeyResultDialog
            objectiveId={objectiveId}
            objectiveOwnerId={ownerId}
            year={year}
            trigger={
              <Button variant="ghost" size="sm" className="gap-1.5 text-xs">
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Add Key Result
              </Button>
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        {sortedKrs.map((kr) => (
          <KrSliderRow key={kr.id} kr={kr} canEdit={canEdit} year={year} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 border-t border-dashed border-[hsl(var(--border))] pt-3">
        <span className="mr-auto text-xs text-[hsl(var(--muted-foreground))]">
          {lastUpdated && (
            <>
              Last KR update {formatRelativeTime(lastUpdated.updated_at)}
              {lastUpdatedAuthorName ? ` by ${lastUpdatedAuthorName}` : ""}
            </>
          )}
        </span>
        {canEdit && (
          <CreateKeyResultDialog
            objectiveId={objectiveId}
            objectiveOwnerId={ownerId}
            year={year}
            trigger={
              <Button variant="ghost" size="sm" className="gap-1.5 text-xs">
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Add Key Result
              </Button>
            }
          />
        )}
        <Button asChild variant="outline" size="sm" className="gap-1.5 text-xs">
          <Link to="/objectives/$id" params={{ id: objectiveId }}>
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            Open full view
          </Link>
        </Button>
      </div>
    </div>
  );
}
